/**
 * Google Analytics 4 + Google Ads conversion tracking.
 *
 * Enable by adding these to /app/frontend/.env (then restart frontend):
 *   REACT_APP_GA_MEASUREMENT_ID=G-XXXXXXXXXX
 *   REACT_APP_GOOGLE_ADS_ID=AW-XXXXXXXXXX
 *   REACT_APP_GOOGLE_ADS_LEAD_LABEL=abcDEF123     (optional — per-conversion label)
 *   REACT_APP_GOOGLE_ADS_WHATSAPP_LABEL=abcDEF124
 *   REACT_APP_GOOGLE_ADS_CALENDLY_LABEL=abcDEF125
 *
 * If neither GA nor Ads IDs are set, this module no-ops safely.
 */
const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
const ADS_ID = process.env.REACT_APP_GOOGLE_ADS_ID;

const LEAD_LABEL = process.env.REACT_APP_GOOGLE_ADS_LEAD_LABEL;
const WHATSAPP_LABEL = process.env.REACT_APP_GOOGLE_ADS_WHATSAPP_LABEL;
const CALENDLY_LABEL = process.env.REACT_APP_GOOGLE_ADS_CALENDLY_LABEL;

let installed = false;

const dataLayerPush = (...args) => {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(args);
};

/**
 * Install the gtag.js snippet once. Safe to call repeatedly.
 */
export function installGtag() {
  if (installed || typeof window === "undefined") return;
  if (!GA_ID && !ADS_ID) return; // no tracking configured

  const primary = GA_ID || ADS_ID;

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${primary}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function (...args) {
    window.dataLayer.push(args);
  };
  window.gtag("js", new Date());

  if (GA_ID) window.gtag("config", GA_ID, { send_page_view: true });
  if (ADS_ID) window.gtag("config", ADS_ID);

  installed = true;
}

/**
 * Track a page view (call on route change).
 */
export function trackPageView(path) {
  if (!GA_ID || typeof window === "undefined" || !window.gtag) return;
  window.gtag("event", "page_view", {
    page_path: path || window.location.pathname + window.location.search,
    page_title: document.title,
  });
}

/**
 * Generic GA4 event.
 */
export function trackEvent(name, params = {}) {
  if (typeof window === "undefined") return;
  dataLayerPush("event", name, params);
  if (window.gtag) window.gtag("event", name, params);
}

/**
 * Fire a Google Ads conversion (label + optional value/currency).
 */
export function trackAdsConversion(label, extras = {}) {
  if (!ADS_ID || !label) return;
  if (typeof window === "undefined" || !window.gtag) return;
  window.gtag("event", "conversion", {
    send_to: `${ADS_ID}/${label}`,
    ...extras,
  });
}

/* ---------- Semantic events ---------- */

export function trackLead(payload = {}) {
  trackEvent("generate_lead", {
    method: payload.method || "consultation_form",
    goal: payload.goal,
    currency: "INR",
    value: 500, // estimated lead value — tune later
    ...payload,
  });
  if (LEAD_LABEL) {
    trackAdsConversion(LEAD_LABEL, {
      currency: "INR",
      value: 500,
      transaction_id: payload.leadId,
    });
  }
}

export function trackWhatsAppClick(payload = {}) {
  trackEvent("whatsapp_click", { source: payload.source || "unknown" });
  if (WHATSAPP_LABEL) trackAdsConversion(WHATSAPP_LABEL, { currency: "INR", value: 250 });
}

export function trackCalendlyOpen(payload = {}) {
  trackEvent("calendly_open", { source: payload.source || "unknown" });
  if (CALENDLY_LABEL) trackAdsConversion(CALENDLY_LABEL, { currency: "INR", value: 750 });
}

export function trackCalendlyBooked(payload = {}) {
  trackEvent("calendly_booked", { source: payload.source || "unknown" });
  if (CALENDLY_LABEL) trackAdsConversion(CALENDLY_LABEL, { currency: "INR", value: 1500 });
}

export function isTrackingEnabled() {
  return Boolean(GA_ID || ADS_ID);
}
